/**
 * Réglage des préférences (docs/14 §4).
 *
 * **L'écran ne connaît pas la cascade.** Il affiche ce que le serveur rend :
 * `enabled` (défauts déjà appliqués) et `editable`. Recalculer ici « in_app ne
 * se coupe pas, le transactionnel non plus » dupliquerait
 * `domain/preferences.py` en TypeScript — deux copies d'une règle qui
 * divergeraient au premier type ajouté, et c'est l'écran qui mentirait en
 * proposant un interrupteur sans effet.
 *
 * Un interrupteur verrouillé est affiché **désactivé plutôt que caché** : une
 * case absente laisse croire à un oubli, une case grisée dit qu'il y a une
 * règle. Le serveur refuse de toute façon (422 `preference_not_editable`).
 */
import { useTranslation } from "react-i18next";

import {
  usePreferences,
  useUpdatePreferences,
  type Preference,
} from "@/features/notifications/api/useNotifications";
import { Alert } from "@/shared/ui/Alert";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

function groupByType(items: Preference[]): Map<string, Preference[]> {
  const grouped = new Map<string, Preference[]>();
  for (const item of items) {
    grouped.set(item.type, [...(grouped.get(item.type) ?? []), item]);
  }
  return grouped;
}

export function NotificationPreferencesPage() {
  const { t } = useTranslation("notifications");
  const { data, isPending, isError, error } = usePreferences();
  const update = useUpdatePreferences();

  return (
    <section className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
        {t("preferences.title")}
      </h1>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{t("preferences.intro")}</p>

      {isPending ? <Skeleton className="mt-6 h-64 w-full" label={t("preferences.title")} /> : null}
      {isError ? <ErrorState error={error} /> : null}
      {update.isError ? (
        <Alert tone="error" className="mt-4">
          {t("preferences.saveFailed")}
        </Alert>
      ) : null}
      {update.isSuccess ? (
        <Alert tone="success" className="mt-4">
          {t("preferences.saved")}
        </Alert>
      ) : null}

      {data !== undefined ? (
        <Card className="mt-6 divide-y divide-slate-200 p-0 dark:divide-slate-700">
          {[...groupByType(data).entries()].map(([type, rows]) => (
            <div key={type} className="p-4">
              <p className="font-medium text-slate-900 dark:text-slate-100">
                {t(`types.${type}.title`)}
              </p>
              <div className="mt-3 flex flex-wrap gap-6">
                {rows.map((row) => (
                  <label
                    key={`${row.type}-${row.channel}`}
                    className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
                    title={row.editable ? undefined : t("preferences.locked")}
                  >
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      disabled={!row.editable || update.isPending}
                      onChange={(event) => {
                        update.mutate([
                          {
                            type: row.type,
                            channel: row.channel,
                            enabled: event.target.checked,
                          },
                        ]);
                      }}
                      data-testid={`pref-${row.type}-${row.channel}`}
                    />
                    {t(`preferences.channel.${row.channel}`)}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </Card>
      ) : null}
    </section>
  );
}
